import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { CreditCard, User, Calendar, RefreshCw, Play, Pause, Search, Shield, Database, AlertTriangle, Plus, Loader2, XCircle, CheckCircle, ExternalLink, X } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { formatDateToDDMMYYYY } from '@/lib/utils';
import { syncSubscriptionStatus, autoSyncSubscriptions } from '@/utils/syncSubscriptionStatus';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";


interface Subscription {
  id: string;
  user_id: string;
  dojo_id: string;
  stripe_customer_id: string;
  plan_type: string;
  is_active: boolean;
  payment_status: string;
  current_period_start: string;
  current_period_end: string;
  created_at: string;
  profiles?: {
    email: string;
    first_name: string;
    last_name: string;
  };
  dojos?: {
    name: string;
    plan: string;
  };
}

const SubscriptionManager: React.FC = () => {
  useUiLanguage();
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [searchEmail, setSearchEmail] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [syncLoading, setSyncLoading] = useState(false);
  const [creatingSubscription, setCreatingSubscription] = useState(false);
  const [newSubscription, setNewSubscription] = useState({
    email: '',
    planType: 'monthly',
    isActive: true
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tableExists, setTableExists] = useState(true);
  const [manualActivationEmail, setManualActivationEmail] = useState('');
  const [dojos, setDojos] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false); // General loading state for actions
  const [subscriptionEndDate, setSubscriptionEndDate] = useState('');
  const [endImmediately, setEndImmediately] = useState(false);
  const [showEndDateDialog, setShowEndDateDialog] = useState(false);


  useEffect(() => {
    fetchSubscriptions();
    fetchDojos();
  }, [statusFilter]);

  // Auto-refresh after successful operations
  useEffect(() => {
    if (subscriptions.length === 0 && !loading) {
      // Auto-refresh if no subscriptions found
      const timer = setTimeout(() => {
        fetchSubscriptions();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [subscriptions.length, loading]);

  const fetchDojos = async () => {
    try {
      const { data, error } = await supabase
        .from('dojos')
        .select('*')
        .order('name');

      if (!error && data) {
        setDojos(data);
      }
    } catch (error) {
      console.error('Error fetching dojos:', error);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if subscriptions table exists
      const { error: tableCheckError } = await supabase
        .from('subscriptions')
        .select('id')
        .limit(1);

      if (tableCheckError?.code === '42P01') {
        console.log('ℹ️ Subscriptions table does not exist');
        setSubscriptions([]);
        setTableExists(false);
        return;
      }

      setTableExists(true);

      // Get all dojos with their admin profiles
      const { data: dojosData, error: dojosError } = await supabase
        .from('dojos')
        .select(`
          id,
          name,
          plan,
          is_active,
          created_at,
          profiles!inner(
            id,
            email,
            first_name,
            last_name,
            role
          )
        `)
        .eq('profiles.role', 'admin')
        .order('created_at', { ascending: false });

      if (dojosError) {
        console.error('Error fetching dojos:', dojosError);
        setError(uiText("Failed to load dojos and admins"));
        return;
      }

      // Get all subscriptions to match with dojos
      const { data: subscriptionsData, error: subscriptionsError } = await supabase
        .from('subscriptions')
        .select('*')
        .order('created_at', { ascending: false });

      if (subscriptionsError) {
        console.error('Error fetching subscriptions:', subscriptionsError);
        setError(uiText("Failed to load subscriptions"));
        return;
      }

      // Create a map of subscriptions by dojo_id for quick lookup
      const subscriptionsByDojo = new Map();
      subscriptionsData?.forEach(sub => {
        if (!subscriptionsByDojo.has(sub.dojo_id) ||
            subscriptionsByDojo.get(sub.dojo_id).created_at < sub.created_at) {
          subscriptionsByDojo.set(sub.dojo_id, sub);
        }
      });

      // Transform dojo data to match subscription structure
      const enrichedSubscriptions = dojosData?.map(dojo => {
        const subscription = subscriptionsByDojo.get(dojo.id);
        const adminProfile = Array.isArray(dojo.profiles) ? dojo.profiles[0] : dojo.profiles;

        return {
          id: subscription?.id || `dojo-${dojo.id}`,
          user_id: adminProfile?.id || '',
          dojo_id: dojo.id,
          stripe_customer_id: subscription?.stripe_customer_id || '',
          plan_type: subscription?.plan_type || 'monthly',
          is_active: subscription?.is_active || false,
          payment_status: subscription?.payment_status || 'pending',
          current_period_start: subscription?.current_period_start || dojo.created_at,
          current_period_end: subscription?.current_period_end || '',
          created_at: subscription?.created_at || dojo.created_at,
          profiles: {
            email: adminProfile?.email || '',
            first_name: adminProfile?.first_name || '',
            last_name: adminProfile?.last_name || ''
          },
          dojos: {
            name: dojo.name,
            plan: dojo.plan
          }
        };
      }) || [];

      const data = enrichedSubscriptions;

      // Apply status filter
      let filteredData = data;
      if (statusFilter && statusFilter !== 'all') {
        if (statusFilter === 'active') {
          filteredData = data.filter(sub => sub.is_active === true);
        } else if (statusFilter === 'inactive') {
          filteredData = data.filter(sub => sub.is_active === false);
        }
      }

      console.log('Fetched subscriptions:', filteredData);
      setSubscriptions(filteredData || []);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      setError(uiText("Failed to load subscriptions"));
    } finally {
      setLoading(false);
    }
  };

  const createSubscription = async () => {
    if (!newSubscription.email) {
      toast({
        title: uiText("Error"),
        description: uiText("Please enter an email address"),
        variant: "destructive"
      });
      return;
    }

    setCreatingSubscription(true);
    try {
      console.log('🆕 Creating subscription for:', newSubscription.email);

      // Use the sync subscription status function
      const result = await syncSubscriptionStatus(newSubscription.email);

      if (result.success) {
        toast({
          title: uiText("Success"),
          description: uiText("Subscription created and activated successfully")
        });
        setNewSubscription({ email: '', planType: 'monthly', isActive: true });
        setDialogOpen(false);
        fetchSubscriptions();
      } else {
        // If sync fails, try to manual creation
        await handleCreateSubscription(newSubscription.email);
      }
    } catch (error: any) {
      console.error('Error creating subscription:', error);
      toast({
        title: uiText("Error"),
        description: error.message || "Failed to create subscription",
        variant: "destructive"
      });
    } finally {
      setCreatingSubscription(false);
    }
  };

  const toggleSubscriptionStatus = async (subscriptionId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('subscriptions')
        .update({
          is_active: !currentStatus,
          payment_status: !currentStatus ? 'active' : 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', subscriptionId);

      if (error) throw error;

      toast({
        title: uiText("Success"),
        description: uiText("Subscription {{value0}} successfully", { value0: !currentStatus ? 'activated' : 'deactivated' })
      });

      fetchSubscriptions();
    } catch (error: any) {
      console.error('Error toggling subscription:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to update subscription status"),
        variant: "destructive"
      });
    }
  };

  const forceSync = async (email: string | undefined) => {
    if (!email) {
      toast({
        title: uiText("Error"),
        description: uiText("Email is required for sync"),
        variant: "destructive"
      });
      return;
    }

    try {
      console.log('🔄 Syncing subscription for:', email);
      setSyncLoading(true);

      // First try the Edge Function
      try {
        const { data, error } = await supabase.functions
          .invoke('manual-subscription-sync', {
            body: { email: email }
          });

        if (!error && data?.success) {
          console.log('✅ Edge Function sync successful:', data);
          toast({
            title: uiText("Success"),
            description: uiText("Subscription synced successfully for {{value0}}", { value0: email })
          });
          fetchSubscriptions();
          return;
        }

        console.warn('❌ Edge Function failed, trying fallback approach:', error);
      } catch (edgeFunctionError) {
        console.warn('❌ Edge Function unavailable, using fallback approach:', edgeFunctionError);
      }

      // Fallback 1: Use the client-side sync function
      try {
        console.log('🔄 Using client-side sync fallback...');
        const result = await syncSubscriptionStatus(email);

        if (result.success) {
          console.log('✅ Client-side sync successful');
          toast({
            title: uiText("Success"),
            description: uiText("Subscription synced successfully for {{value0}}", { value0: email })
          });
          fetchSubscriptions();
          return;
        }
      } catch (clientSyncError) {
        console.warn('❌ Client-side sync failed:', clientSyncError);
      }

      // Fallback 2: Direct database sync
      try {
        console.log('🔧 Using direct database sync...');
        const result = await directDatabaseSync(email);

        if (result.success) {
          toast({
            title: uiText("Success"),
            description: result.message || `Subscription synced for ${email}`
          });
          fetchSubscriptions();
          return;
        }
      } catch (directSyncError) {
        console.warn('❌ Direct database sync failed:', directSyncError);
      }

      // Final fallback: Manual creation
      console.log('🔧 All sync methods failed, trying manual creation...');
      await handleCreateSubscription(email);

    } catch (error: any) {
      console.error('Error syncing subscription:', error);

      toast({
        title: uiText("Error"),
        description: error.message || "Failed to sync subscription",
        variant: "destructive"
      });
    } finally {
      setSyncLoading(false);
    }
  };


  const handleToggleSubscription = async (subscription: Subscription, activate: boolean) => {
    try {
      const { error } = await supabase
        .from('subscriptions')
        .update({
          is_active: activate,
          payment_status: activate ? 'paid' : 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', subscription.id);

      if (error) throw error;

      // Update dojo plan
      const { error: dojoError } = await supabase
        .from('dojos')
        .update({
          plan: activate ? 'pro' : 'trial',
          is_active: activate,
          updated_at: new Date().toISOString()
        })
        .eq('id', subscription.dojo_id)
        .neq('name', 'Taiga Dojo'); // Preserve Taiga Dojo

      if (dojoError) throw dojoError;

      // Update user payment status
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          payment_status: activate ? 'paid' : 'pending',
          updated_at: new Date().toISOString()
        })
        .eq('id', subscription.user_id);

      if (profileError) throw profileError;

      toast({
        title: uiText("Success"),
        description: uiText("Subscription {{value0}} successfully", { value0: activate ? 'activated' : 'deactivated' }),
      });

      fetchSubscriptions();
    } catch (error: any) {
      console.error('Error toggling subscription:', error);
      toast({
        title: uiText("Error"),
        description: error.message || 'Failed to toggle subscription',
        variant: 'destructive'
      });
    }
  };

  const handleSyncByEmail = async () => {
    if (!searchEmail.trim()) {
      toast({
        title: uiText("Error"),
        description: uiText("Please enter an email address"),
        variant: 'destructive'
      });
      return;
    }

    setSyncLoading(true);
    try {
      const success = await syncSubscriptionStatus(searchEmail.trim());

      if (success) {
        toast({
          title: uiText("Success"),
          description: uiText("Subscription synced successfully for {{value0}}", { value0: searchEmail }),
        });
        setSearchEmail(''); // Clear search email after successful sync
        fetchSubscriptions();
      } else {
        // If sync fails, try to create subscription manually
        await handleCreateSubscription(searchEmail.trim());
      }
    } catch (error) {
      toast({
        title: uiText("Error"),
        description: uiText("Failed to sync subscription"),
        variant: 'destructive'
      });
    } finally {
      setSyncLoading(false);
    }
  };

  const handleCreateSubscription = async (email: string) => {
    try {
      // Get user profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, dojo_id, payment_status')
        .eq('email', email)
        .single();

      if (profileError || !profile) {
        toast({
          title: uiText("Error"),
          description: uiText("User not found"),
          variant: 'destructive'
        });
        return;
      }

      // Create subscription record
      const { error: createError } = await supabase
        .from('subscriptions')
        .insert({
          user_id: profile.id,
          dojo_id: profile.dojo_id,
          plan_type: 'monthly',
          is_active: true,
          payment_status: 'paid',
          current_period_start: new Date().toISOString(),
          current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          created_at: new Date().toISOString()
        });

      if (createError) throw createError;

      // Update dojo to pro plan
      const { error: dojoError } = await supabase
        .from('dojos')
        .update({
          plan: 'pro',
          is_active: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', profile.dojo_id)
        .neq('name', 'Taiga Dojo');

      if (dojoError) throw dojoError;

      // Update user payment status
      const { error: profileUpdateError } = await supabase
        .from('profiles')
        .update({
          payment_status: 'paid',
          updated_at: new Date().toISOString()
        })
        .eq('id', profile.id);

      if (profileUpdateError) throw profileUpdateError;

      toast({
        title: uiText("Success"),
        description: uiText("Subscription created successfully for {{value0}}", { value0: email }),
      });

      fetchSubscriptions();
    } catch (error: any) {
      console.error('Error creating subscription:', error);
      toast({
        title: uiText("Error"),
        description: error.message || 'Failed to create subscription',
        variant: 'destructive'
      });
    }
  };

  const handleAutoSync = async () => {
    setIsLoading(true);
    try {
      await autoSyncSubscriptions();
      toast({
        title: uiText("Success"),
        description: uiText("Auto-sync completed successfully"),
      });
      fetchSubscriptions();
    } catch (error) {
      toast({
        title: uiText("Error"),
        description: uiText("Auto-sync failed"),
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadgeVariant = (isActive: boolean, paymentStatus: string) => {
    if (isActive && paymentStatus === 'paid') return 'default';
    if (paymentStatus === 'cancelled') return 'destructive';
    return 'secondary';
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    const emailMatch = !searchEmail ||
      sub.profiles?.email.toLowerCase().includes(searchEmail.toLowerCase());

    const statusMatch = statusFilter === 'all' ||
      (statusFilter === 'active' && sub.is_active) ||
      (statusFilter === 'inactive' && !sub.is_active);

    return emailMatch && statusMatch;
  });

  const handleSyncSubscription = async (email: string) => {
    setIsLoading(true);
    const success = await syncSubscriptionStatus(email);
    if (success) {
      toast({
        title: uiText("Success"),
        description: uiText("Subscription synced successfully"),
      });
      fetchSubscriptions();
    } else {
      toast({
        title: uiText("Error"),
        description: uiText("Failed to sync subscription"),
        variant: "destructive"
      });
    }
    setIsLoading(false);
  };

  const handleActivateSubscription = async () => {
    if (!manualActivationEmail.trim()) {
      toast({
        title: uiText("Error"),
        description: uiText("Please enter an email address"),
        variant: "destructive"
      });
      return;
    }
    setIsLoading(true);
    try {
      // Find the user profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('email', manualActivationEmail.trim())
        .single();

      if (profileError || !profile) {
        toast({
          title: uiText("Error"),
          description: uiText("User not found with this email"),
          variant: "destructive"
        });
        return;
      }

      // Update user payment status
      await supabase
        .from('profiles')
        .update({ payment_status: 'complete' })
        .eq('id', profile.id);

      // If user is admin, activate their dojo
      if (profile.role === 'admin' && profile.dojo_id) {
        await supabase
          .from('dojos')
          .update({
            plan: 'pro',
            is_active: true
          })
          .eq('id', profile.dojo_id);
      }

      toast({
        title: uiText("Success"),
        description: uiText("Subscription activated for {{value0}}", { value0: manualActivationEmail }),
      });

      setManualActivationEmail('');
      fetchSubscriptions();
      fetchDojos();
    } catch (error) {
      console.error('Error activating subscription:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to activate subscription"),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeactivateSubscription = async () => {
    if (!manualActivationEmail.trim()) {
      toast({
        title: uiText("Error"),
        description: uiText("Please enter an email address"),
        variant: "destructive"
      });
      return;
    }
    setIsLoading(true);
    try {
      // Find the user profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('email', manualActivationEmail.trim())
        .single();

      if (profileError || !profile) {
        toast({
          title: uiText("Error"),
          description: uiText("User not found with this email"),
          variant: "destructive"
        });
        return;
      }

      // Calculate end date
      let endDate = new Date();
      if (!endImmediately && subscriptionEndDate) {
        endDate = new Date(subscriptionEndDate);
      }

      // Update subscription if exists
      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', profile.id)
        .maybeSingle();

      if (subscription) {
        await supabase
          .from('subscriptions')
          .update({
            is_active: endImmediately,
            payment_status: endImmediately ? 'cancelled' : 'active',
            current_period_end: endDate.toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', subscription.id);
      }

      // Update user payment status
      await supabase
        .from('profiles')
        .update({
          payment_status: endImmediately ? 'pending' : 'complete',
          updated_at: new Date().toISOString()
        })
        .eq('id', profile.id);

      // If user is admin, update their dojo
      if (profile.role === 'admin' && profile.dojo_id) {
        await supabase
          .from('dojos')
          .update({
            plan: endImmediately ? 'trial' : 'pro',
            is_active: endImmediately ? false : true,
            updated_at: new Date().toISOString()
          })
          .eq('id', profile.dojo_id);
      }

      const actionText = endImmediately
        ? 'deactivated immediately'
        : `scheduled to end on ${endDate.toLocaleDateString(currentLocale())}`;

      toast({
        title: uiText("Success"),
        description: uiText("Subscription {{value0}} for {{value1}}", { value0: actionText, value1: manualActivationEmail }),
      });

      setManualActivationEmail('');
      setSubscriptionEndDate('');
      setEndImmediately(false);
      setShowEndDateDialog(false);
      fetchSubscriptions();
      fetchDojos();
    } catch (error) {
      console.error('Error deactivating subscription:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to deactivate subscription"),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAutoSyncRecent = async () => {
    setIsLoading(true);
    try {
      console.log('🔄 Running auto-sync for recent payments...');

      const { data, error } = await supabase.functions.invoke('manual-subscription-sync', {
        body: { email: 'auto-sync-all' } // Special trigger for bulk sync
      });

      if (error) {
        toast({
          title: uiText("Error"),
          description: uiText("Auto-sync failed"),
          variant: "destructive"
        });
      } else {
        toast({
          title: uiText("Success"),
          description: uiText("Auto-sync completed for recent payments")
        });
      }
    } catch (error) {
      console.error('❌ Auto-sync error:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Auto-sync failed"),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchSubscriptions();
  };

  const handleAutoSyncAll = () => {
    handleAutoSync();
  };

  const manualDeactivateSubscription = async () => {
    await handleDeactivateSubscription();
  };

  const directDatabaseSync = async (email: string) => {
    try {
      console.log('🔧 Direct database sync for:', email);

      // Get user profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, dojo_id, role, email')
        .ilike('email', email.trim().toLowerCase())
        .single();

      if (profileError || !profile) {
        throw new Error(uiText("User not found"));
      }

      // Only process admins
      if (profile.role !== 'admin' && profile.role !== 'superadmin') {
        throw new Error(uiText("Only admins can have subscriptions"));
      }

      // Check if subscription already exists
      const { data: existingSub } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', profile.id)
        .eq('is_active', true)
        .maybeSingle();

      if (existingSub) {
        console.log('✅ Active subscription already exists');
        return { success: true, message: uiText("Subscription already active") };
      }

      // Create new subscription
      const { error: createError } = await supabase
        .from('subscriptions')
        .insert({
          user_id: profile.id,
          dojo_id: profile.dojo_id,
          plan_type: 'monthly',
          is_active: true,
          payment_status: 'paid',
          current_period_start: new Date().toISOString(),
          current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        });

      if (createError) throw createError;

      // Update user and dojo status
      await supabase
        .from('profiles')
        .update({ payment_status: 'complete' })
        .eq('id', profile.id);

      if (profile.dojo_id) {
        await supabase
          .from('dojos')
          .update({
            plan: 'pro',
            is_active: true,
            updated_at: new Date().toISOString()
          })
          .eq('id', profile.dojo_id)
          .neq('name', 'Taiga Dojo');
      }

      console.log('✅ Direct database sync completed');
      return { success: true, message: uiText("Subscription created successfully") };

    } catch (error: any) {
      console.error('❌ Direct database sync failed:', error);
      throw error;
    }
  };


  return (
    <div className="space-y-4 md:space-y-6 p-3 md:p-6 bg-gray-50 min-h-screen">
      <div className="flex flex-col sm:flex-row gap-3 md:gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800">{uiText("Subscription Management")}</h2>
          <p className="text-sm md:text-base text-gray-600">{uiText("Manage and monitor all subscriptions")}</p>
          {!tableExists && (
            <div className="flex items-center gap-2 mt-2">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              <span className="text-yellow-500 text-sm">{uiText("Subscriptions table not found - using manual mode")}</span>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            onClick={() => autoSyncSubscriptions()}
            variant="outline"
            className="border-blue-500 text-blue-600 hover:bg-blue-50 h-10"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {uiText("Auto Sync All")}</Button>
        </div>
      </div>

      {/* Manual Activation Controls */}
      <Card className="w-full border-0 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-3 text-xl">
            <Shield className="h-6 w-6" />
            {uiText("Manual Subscription Control")}</CardTitle>
          <CardDescription className="text-blue-100 text-base">
            {uiText("Activate or deactivate subscriptions manually")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 md:space-y-6 p-4 md:p-6">
          {/* Enhanced Email Input Section */}
          <div className="bg-gray-50 p-3 md:p-4 rounded-lg border">
            <Label htmlFor="manual-email" className="text-sm font-semibold text-gray-700 mb-2 block">
              {uiText("User Email Address")}</Label>
            <div className="space-y-3">
              <Input
                id="manual-email"
                type="email"
                placeholder={uiText("Enter user email address to manage subscription")}
                value={manualActivationEmail}
                onChange={(e) => setManualActivationEmail(e.target.value)}
                className="w-full h-12 text-base border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Button
                  onClick={handleActivateSubscription}
                  disabled={!manualActivationEmail.trim() || isLoading}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 h-12 w-full"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {uiText("Activating...")}</>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      {uiText("Activate Subscription")}</>
                  )}
                </Button>
                <Button
                  onClick={handleAutoSyncRecent}
                  disabled={isLoading}
                  variant="outline"
                  className="border-blue-500 text-blue-600 hover:bg-blue-50 px-4 py-2 h-12 w-full"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {uiText("Auto-Sync Recent")}</Button>

                <Dialog open={showEndDateDialog} onOpenChange={setShowEndDateDialog}>
                  <DialogTrigger asChild>
                    <Button
                      variant="destructive"
                      disabled={!manualActivationEmail.trim() || isLoading}
                      className="px-4 py-2 h-12 w-full"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {uiText("Processing...")}</>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 mr-2" />
                          {uiText("End Subscription")}</>
                      )}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white border-gray-200 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-gray-800">{uiText("End Subscription")}</DialogTitle>
                      <DialogDescription className="text-gray-600">
                        {uiText("Choose when to end the subscription for")}{' '}{manualActivationEmail}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="immediate"
                          name="endOption"
                          checked={endImmediately}
                          onChange={() => setEndImmediately(true)}
                          className="h-4 w-4 text-red-600"
                        />
                        <Label htmlFor="immediate" className="text-gray-700">
                          {uiText("End immediately")}</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="scheduled"
                          name="endOption"
                          checked={!endImmediately}
                          onChange={() => setEndImmediately(false)}
                          className="h-4 w-4 text-red-600"
                        />
                        <Label htmlFor="scheduled" className="text-gray-700">
                          {uiText("Schedule end date")}</Label>
                      </div>
                      {!endImmediately && (
                        <div className="ml-6">
                          <Label htmlFor="endDate" className="text-sm text-gray-600">
                            {uiText("End Date")}</Label>
                          <Input
                            id="endDate"
                            type="date"
                            value={subscriptionEndDate}
                            onChange={(e) => setSubscriptionEndDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className="mt-1"
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setShowEndDateDialog(false)}
                        className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                      >
                        {uiText("Cancel")}</Button>
                      <Button
                        onClick={manualDeactivateSubscription}
                        disabled={!endImmediately && !subscriptionEndDate}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        {endImmediately ? uiText("End Now") : uiText("Schedule End")}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Controls */}
      <Card className="w-full border-0 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-3 text-xl">
            <CreditCard className="h-6 w-6" />
            {uiText("Subscription Management")}</CardTitle>
          <CardDescription className="text-blue-100 text-base">
            {uiText("Manage user subscriptions and payment statuses with ease")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 md:space-y-6 p-4 md:p-6">
          {/* Enhanced Subscription Management Section */}
          <div className="space-y-4 md:space-y-6">
            <div className="bg-white border rounded-lg p-3 md:p-4 shadow-sm">
              <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-800">
                    {uiText("Dojos & Admin Subscriptions")}<span className="ml-2 bg-blue-100 text-blue-800 px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium">
                      {subscriptions.length} {' '}{uiText("Total")}</span>
                  </h3>
                  <p className="text-gray-600 text-xs md:text-sm mt-1">{uiText("Monitor and manage all subscription statuses")}</p>
                </div>

                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 w-full">
                  {/* Search and primary actions */}
                  <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto sm:flex-1">
                    <Input
                      placeholder={uiText("Search by email or sync user")}
                      value={searchEmail}
                      onChange={(e) => setSearchEmail(e.target.value)}
                      className="w-full sm:w-64 h-11 border-gray-300 focus:border-blue-500"
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleSyncSubscription(searchEmail)}
                        disabled={!searchEmail || isLoading}
                        variant="outline"
                        className="flex-1 sm:flex-none border-blue-500 text-blue-600 hover:bg-blue-50 h-11"
                      >
                        <Search className="h-4 w-4 sm:mr-1" />
                        <span className="hidden sm:inline">{uiText("Sync")}</span>
                      </Button>
                      <Button
                        onClick={() => handleCreateSubscription(searchEmail)}
                        disabled={!searchEmail || isLoading}
                        className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 text-white h-11"
                      >
                        <Plus className="h-4 w-4 sm:mr-1" />
                        <span className="hidden sm:inline">{uiText("Create")}</span>
                      </Button>
                    </div>
                  </div>

                  {/* Filter and secondary actions */}
                  <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-full sm:w-48 h-11 border-gray-300">
                        <SelectValue placeholder={uiText("Filter by status")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{uiText("All Subscriptions")}</SelectItem>
                        <SelectItem value="active">{uiText("Active Only")}</SelectItem>
                        <SelectItem value="inactive">{uiText("Inactive Only")}</SelectItem>
                        <SelectItem value="trial">{uiText("Trial Only")}</SelectItem>
                        <SelectItem value="canceled">{uiText("Canceled Only")}</SelectItem>
                      </SelectContent>
                    </Select>

                    <div className="flex gap-2">
                      <Button
                        onClick={handleAutoSyncAll}
                        disabled={isLoading}
                        variant="outline"
                        className="flex-1 sm:flex-none border-purple-500 text-purple-600 hover:bg-purple-50 h-11 whitespace-nowrap"
                      >
                        <RefreshCw className="h-4 w-4 sm:mr-1" />
                        <span className="hidden sm:inline">{uiText("Auto-Sync All")}</span>
                        <span className="sm:hidden">{uiText("Auto-Sync")}</span>
                      </Button>

                      <Button
                        onClick={handleRefresh}
                        disabled={isLoading}
                        variant="outline"
                        className="flex-1 sm:flex-none border-gray-400 text-gray-600 hover:bg-gray-50 h-11"
                      >
                        <RefreshCw className="h-4 w-4 sm:mr-1" />
                        <span className="hidden sm:inline">{uiText("Refresh")}</span>
                      </Button>
                    </div>

                    <Button
                      onClick={() => window.open('https://buy.stripe.com/fZu5kD06o4U37Ha8WTdAk00', '_blank')}
                      className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white h-11"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {uiText("Payment Link")}</Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscriptions Table */}
      <Card className="w-full border-0 shadow-lg">
        <CardHeader className="bg-white rounded-lg">
          <CardTitle className="text-gray-800 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-blue-600" />
              {uiText("Subscription Details")}</span>
            {!tableExists && (
              <Badge variant="secondary" className="text-xs">
                {uiText("Manual Mode")}</Badge>
            )}
          </CardTitle>
          <CardDescription className="text-gray-600">
            {uiText("View detailed information about each dojo's subscription status")}</CardDescription>
          {error && (
            <p className="text-red-500 text-sm mt-2">{error}</p>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 text-blue-500 animate-spin mx-auto" />
              <p className="mt-2 text-gray-500">{uiText("Loading subscriptions...")}</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-4">
                {filteredSubscriptions.map((subscription) => (
                  <Card key={subscription.id} className="border-2 border-gray-200">
                    <CardContent className="p-4 space-y-3">
                      {/* Admin Info */}
                      <div>
                        <div className="text-xs text-gray-500 font-medium mb-1">{uiText("Dojo Admin")}</div>
                        <div className="font-semibold text-gray-900">
                          {subscription.profiles?.first_name} {subscription.profiles?.last_name}
                        </div>
                        <div className="text-sm text-gray-600">{subscription.profiles?.email}</div>
                      </div>

                      {/* Dojo Info */}
                      <div>
                        <div className="text-xs text-gray-500 font-medium mb-1">{uiText("Dojo")}</div>
                        <div className="font-medium text-gray-800">{subscription.dojos?.name}</div>
                        <Badge variant={subscription.dojos?.plan === 'pro' ? 'default' : 'secondary'} className="text-xs mt-1">
                          {subscription.dojos?.plan}
                        </Badge>
                      </div>

                      {/* Plan and Status */}
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-xs text-gray-500 font-medium mb-1">{uiText("Subscription Plan")}</div>
                          <Badge variant="outline" className="text-xs">{subscription.plan_type}</Badge>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 font-medium mb-1">{uiText("Status")}</div>
                          <Badge variant={getStatusBadgeVariant(subscription.is_active, subscription.payment_status)}>
                            {subscription.is_active ? uiText("Active") : uiText("Inactive")}
                          </Badge>
                        </div>
                      </div>

                      {/* Period End */}
                      <div>
                        <div className="text-xs text-gray-500 font-medium mb-1">{uiText("Period End")}</div>
                        <div className="text-sm text-gray-700">
                          {subscription.current_period_end ? formatDateToDDMMYYYY(subscription.current_period_end) : '-'}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="pt-2 border-t flex gap-2">
                        <Button
                          size="sm"
                          variant={subscription.is_active ? "destructive" : "default"}
                          onClick={() => toggleSubscriptionStatus(subscription.id, subscription.is_active)}
                          className="flex-1 h-10"
                        >
                          {subscription.is_active ? (
                            <>
                              <XCircle className="h-4 w-4 mr-1" />
                              {uiText("Deactivate")}</>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-1" />
                              {uiText("Activate")}</>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => forceSync(subscription.profiles?.email)}
                          className="flex-1 h-10 border-blue-500 text-blue-600 hover:bg-blue-50"
                        >
                          <Loader2 className="h-4 w-4 mr-1" />
                          {uiText("Sync")}</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {filteredSubscriptions.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    {uiText("No subscriptions found matching your criteria.")}</div>
                )}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block border rounded-lg overflow-hidden shadow-sm bg-white">
                <Table>
                <TableHeader>
                  <TableRow className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                    <TableHead className="font-bold text-gray-700 py-4">{uiText("Dojo Admin")}</TableHead>
                    <TableHead className="font-bold text-gray-700 py-4">{uiText("Dojo")}</TableHead>
                    <TableHead className="font-bold text-gray-700 py-4">{uiText("Subscription Plan")}</TableHead>
                    <TableHead className="font-bold text-gray-700 py-4">{uiText("Payment Status")}</TableHead>
                    <TableHead className="font-bold text-gray-700 py-4">{uiText("Period End")}</TableHead>
                    <TableHead className="font-bold text-gray-700 text-center py-4">{uiText("Actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubscriptions.map((subscription) => (
                    <TableRow key={subscription.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <TableCell className="py-4">
                        <div>
                          <div className="font-medium text-gray-800">
                            {subscription.profiles?.first_name} {subscription.profiles?.last_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {subscription.profiles?.email}
                          </div>
                        </div>
                      </TableCell>

                      <TableCell className="py-4">
                        <div>
                          <div className="font-medium text-gray-800">
                            {subscription.dojos?.name}
                          </div>
                          <Badge variant={subscription.dojos?.plan === 'pro' ? 'default' : 'secondary'} className="text-xs font-normal">
                            {subscription.dojos?.plan}
                          </Badge>
                        </div>
                      </TableCell>

                      <TableCell className="py-4">
                        <Badge variant="outline" className="text-xs font-normal">
                          {subscription.plan_type}
                        </Badge>
                      </TableCell>

                      <TableCell className="py-4">
                        <div className="flex items-center gap-2">
                          <Badge variant={getStatusBadgeVariant(subscription.is_active, subscription.payment_status)}>
                            {subscription.is_active ? uiText("Active") : uiText("Inactive")}
                          </Badge>
                          <Button
                            size="sm"
                            variant={subscription.is_active ? "destructive" : "default"}
                            onClick={() => toggleSubscriptionStatus(subscription.id, subscription.is_active)}
                            className={`gap-1 h-8 px-2 ${subscription.is_active ? 'hover:bg-red-600' : 'hover:bg-green-600'}`}
                          >
                            {subscription.is_active ? (
                              <>
                                <XCircle className="h-3 w-3" />
                                {uiText("Deactivate")}</>
                            ) : (
                              <>
                                <CheckCircle className="h-3 w-3" />
                                {uiText("Activate")}</>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => forceSync(subscription.profiles?.email)}
                            className="gap-1 h-8 px-2 border-blue-500 text-blue-600 hover:bg-blue-50"
                          >
                            <Loader2 className="h-3 w-3" />
                            {uiText("Sync")}</Button>
                        </div>
                      </TableCell>

                      <TableCell className="py-4 text-gray-700">
                        {subscription.current_period_end
                          ? formatDateToDDMMYYYY(subscription.current_period_end)
                          : '-'
                        }
                      </TableCell>

                      <TableCell className="py-4 text-center">
                        <div className="flex justify-center gap-2">
                          {subscription.is_active ? (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="destructive" className="h-8 px-2">
                                  <Pause className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-white border-gray-200">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-gray-800">{uiText("Confirm Deactivation")}</AlertDialogTitle>
                                  <AlertDialogDescription className="text-gray-600">
                                    {uiText("Are you sure you want to deactivate this subscription? This will downgrade the dojo to trial mode.")}</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="bg-gray-100 text-gray-700 hover:bg-gray-200">{uiText("Cancel")}</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleToggleSubscription(subscription, false)}
                                    className="bg-red-600 hover:bg-red-700 text-white"
                                  >
                                    {uiText("Deactivate")}</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          ) : (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="default" className="h-8 px-2 bg-green-600 hover:bg-green-700">
                                  <Play className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-white border-gray-200">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-gray-800">{uiText("Confirm Activation")}</AlertDialogTitle>
                                  <AlertDialogDescription className="text-gray-600">
                                    {uiText("Are you sure you want to activate this subscription? This will upgrade the dojo to pro plan.")}</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="bg-gray-100 text-gray-700 hover:bg-gray-200">{uiText("Cancel")}</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleToggleSubscription(subscription, true)}
                                    className="bg-green-600 hover:bg-green-700 text-white"
                                  >
                                    {uiText("Activate")}</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}

                  {filteredSubscriptions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        {uiText("No subscriptions found matching your criteria.")}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionManager;